import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { ResponsiveBar } from "@nivo/bar";
import { CompassParagraph } from "../../components/common/styledComponents";
import {
  ElementContainer,
  FlexibleContainer,
} from "../../components/containers/styledComponents";
import { LegendCircle } from "../../components/visuals/styledComponents";
import { darkGreen, graphPalette, lightGreyText } from "../../styles";
import { GetControlPanelDataParams } from "../../types/controlPanelTypes";
import { usePost } from "../../hooks/usePost";
import { formatPct } from "../../utils/formattingUtils";

const MainContainer = styled.div`
  overflow: hidden;
`;

const StyledCompassParagraph = styled(CompassParagraph)`
  overflow-y: auto;
  height: 60px;
  padding: 4px 0px;
  max-height: 100%;
  width: 100%;
`;

enum CHART_TITLES {
  REVENUE_WEIGHTED_EXPOSURE = "Revenue weighted exposure",
  VALUE_OF_HOLDINGS_STRUCTURE = "Value of holdings exposure",
}
const chartTitles = [
  CHART_TITLES.VALUE_OF_HOLDINGS_STRUCTURE,
  CHART_TITLES.REVENUE_WEIGHTED_EXPOSURE,
];

const mockData: FossilFuelExposureData[] = [
  {
    index: "Fossil Fuel Exposure",
    [CHART_TITLES.REVENUE_WEIGHTED_EXPOSURE]: 0.01,
    [CHART_TITLES.VALUE_OF_HOLDINGS_STRUCTURE]: 0.005,
  },
];

const transformData = (
  inputData: { metric_id: string; weighted_average: number }[],
): FossilFuelExposureData => {
  // Reduce the array into a single object
  const transformed = inputData.reduce((acc, current) => {
    const key =
      current.metric_id === "fossil_fuel_exposure_pct"
        ? CHART_TITLES.VALUE_OF_HOLDINGS_STRUCTURE
        : CHART_TITLES.REVENUE_WEIGHTED_EXPOSURE;
    acc[key] = Math.max(current.weighted_average, 0.000001);
    return acc;
  }, {} as FossilFuelExposureData);

  // Assume the 'index' is a known or static value, or needs to be set from somewhere
  transformed.index = "Your Index Value"; // Set this value as needed

  return transformed;
};

export const FossilFuelExposure = ({
  params,
}: FossilFuelExposureProps): JSX.Element => {
  const [data, setData] = useState<FossilFuelExposureData[]>(mockData);
  const { data: fossilFuelExposureData, sendData: getFossilFuelExposureData } =
    usePost<
      { metric_id: string; weighted_average: number }[],
      GetControlPanelDataParams
    >();

  useEffect(() => {
    getFossilFuelExposureData("GET_PORTFOLIOS_FOSSIL_FUEL_EXPOSURE", params);
  }, [params]);

  useEffect(() => {
    if (fossilFuelExposureData) {
      const transformedData = transformData(fossilFuelExposureData);
      setData([transformedData]);
    }
  }, [fossilFuelExposureData]);

  const filteredKeys = data.length
    ? Object.keys(data[0]).filter((key) => key !== "index")
    : [];

  return (
    <MainContainer>
      <ElementContainer>
        <span>
          <CompassParagraph
            color={darkGreen}
            fontSize={14}
            bold={true}
            mb={10}
            mr={1}
          >
            Fossil Fuel Exposure
          </CompassParagraph>
          <StyledCompassParagraph>
            {filteredKeys.map((key, index) => (
              <ElementContainer key={index} mb={3}>
                <LegendCircle color={graphPalette[index]}></LegendCircle>
                <CompassParagraph color="#8B909A" fontSize={12}>
                  {key}
                </CompassParagraph>
              </ElementContainer>
            ))}
          </StyledCompassParagraph>
        </span>
        <ElementContainer h="100px" w="130px" ai="center" jc="center">
          {chartTitles.map((title, index) => (
            <ElementContainer
              key={index}
              h="100px"
              w="100px"
              flexDirection="column"
            >
              <FlexibleContainer w="100%" jc="center">
                <CompassParagraph color={graphPalette[index]} mr={20}>
                  {formatPct(data?.[0]?.[title] ?? 0)}
                </CompassParagraph>
              </FlexibleContainer>
              <BarChart data={data} title={title} />
            </ElementContainer>
          ))}
        </ElementContainer>
      </ElementContainer>
    </MainContainer>
  );
};

type FossilFuelExposureProps = {
  data?: FossilFuelExposureData[];
  params: GetControlPanelDataParams;
};

type BarChartProps = {
  title: string;
  data: FossilFuelExposureData[];
};

type FossilFuelExposureData = {
  index: string;
  "Revenue weighted exposure": number;
  "Value of holdings exposure": number;
};

const BarChart = ({ title, data }: BarChartProps): JSX.Element => {
  const maxValue = data.length
    ? data[0][CHART_TITLES.VALUE_OF_HOLDINGS_STRUCTURE]
    : 0;
  return (
    <>
      <ResponsiveBar
        data={data || []}
        tooltip={(): JSX.Element => <></>}
        keys={[title]}
        indexBy="index"
        enableLabel={false}
        colors={[graphPalette[title === chartTitles[0] ? 0 : 1]]}
        margin={{ top: 0, right: 25, bottom: 5, left: 0 }}
        axisTop={null}
        axisLeft={null}
        axisBottom={null}
        valueScale={{ type: "linear", min: 0, max: maxValue }}
        indexScale={{ type: "band", round: true }}
        axisRight={{
          tickValues: Array.from({ length: 11 }, (_, index) => index * 10),
          tickSize: 2,
          tickPadding: 2,
          tickRotation: 0,
          format: (value: number) => `${value}%`,
        }}
        animate={true}
        theme={theme}
      />
    </>
  );
};
const theme = {
  axis: {
    ticks: {
      text: {
        fill: lightGreyText,
        fontSize: 10,
        fontFamily: "Apercu",
      },
    },
  },
  grid: {
    line: {},
  },
};
